import React from 'react';
import {
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Paper,
  Typography,
  CircularProgress,
  Button,
  Box,
} from '@mui/material';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import type { IUser } from 'api/services/dataEndpoints';
import { useLazyGetUsersQuery } from 'api/services/dataEndpoints';
import type { UserStatusDisplayType } from 'types/filtersType';

interface IUsersListProps {
  selectedUserId: number | null;
  onUserSelect: (user: IUser | null) => void;
  users?: IUser[];
  userStatus: UserStatusDisplayType;
}

const UsersList: React.FC<IUsersListProps> = ({
  selectedUserId,
  onUserSelect,
  userStatus,
}) => {
  const [triggerGetUsers, { data: users = [], isLoading, error }] = useLazyGetUsersQuery();
  const [showManagers, setShowManagers] = React.useState(false);

  React.useEffect(() => {
    const userStatusPayload = userStatus || null;
    triggerGetUsers(userStatusPayload);
  }, [userStatus, triggerGetUsers]);

  React.useEffect(() => {
    const targetUser = users.find((user) => user.id === selectedUserId);
    if (!targetUser && selectedUserId) {
      onUserSelect(null);
    }
  }, [users, selectedUserId, onUserSelect]);

  const isManager = (user: IUser): boolean => {
    return user.is_staff === true;
  };

  const filteredUsers = React.useMemo(() => {
    if (!showManagers) {
      return users.filter((user) => !isManager(user));
    }
    return users;
  }, [users, showManagers]);

  if (isLoading) {
    return (
      <Paper
        elevation={3}
        sx={{
          padding: 2,
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Paper>
    );
  }

  if (error) {
    console.log('error', error);
    return (
      <Paper elevation={3} sx={{ padding: 2, height: '100%' }}>
        <Typography color="error">Cannot load users</Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ padding: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
          Users
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setShowManagers(!showManagers)}
        >
          {showManagers ? 'Hide Managers' : 'Show Managers'}
        </Button>
      </Box>
      <List>
        {filteredUsers.map((user) => (
          <React.Fragment key={user.id}>
            <ListItem
              button
              onClick={() => onUserSelect(user)}
              selected={selectedUserId === user.id}
            >
              {isManager(user) && (
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <ManageAccountsIcon color="primary" />
                </ListItemIcon>
              )}
              <ListItemText primary={`${user.custom_data?.name || ''} (${user.email})`} />
            </ListItem>
            <Divider />
          </React.Fragment>
        ))}
      </List>
    </Paper>
  );
};

export default UsersList;
